import { useState, useMemo, memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { FaChevronDown } from 'react-icons/fa';
import { getAirFareRulesRequest } from '../../../../store/slices/flight';
import { SearchStatus } from '../../../../constants';
import FlightFareRulesModal from '../../../CruiseBooking/PolicyAndFees/FlightFareRules/FlightFareRulesModal';

const FareRulesLink = ({ flightItinerary, tripId }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasCheckedRules, setHasCheckedRules] = useState(false);

  const airFareRulesStatus = useSelector(
    (store) => store.flight.airFareRulesStatus[tripId] || SearchStatus.INITIAL,
  );

  const airFareRulesList = useSelector(
    (store) => (store.flight.airFareRulesList && store.flight.airFareRulesList[tripId]) || [],
  );

  const hasRulesData = useMemo(() => {
    if (!airFareRulesList || airFareRulesList.length === 0) return false;
    return airFareRulesList.some(
      (fareRule) =>
        fareRule.fareRulesGroups &&
        fareRule.fareRulesGroups.length > 0 &&
        fareRule.fareRulesGroups.some(
          (group) =>
            (group.quickRules && group.quickRules.length > 0) ||
            (group.fareRuleInfo && group.fareRuleInfo.length > 0),
        ),
    );
  }, [airFareRulesList]);

  const isLoading = airFareRulesStatus === SearchStatus.SEARCHING;

  const handleClick = () => {
    if (isLoading) return;

    if (hasRulesData) {
      setIsModalOpen(true);
      return;
    }

    setHasCheckedRules(true);
    dispatch(
      getAirFareRulesRequest({
        flightItinerary,
        tripId,
      }),
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setHasCheckedRules(false);
  };

  useEffect(() => {
    if (
      hasCheckedRules &&
      !isModalOpen &&
      (airFareRulesStatus === SearchStatus.DONE || airFareRulesStatus === SearchStatus.ERROR)
    ) {
      setIsModalOpen(true);
    }
  }, [hasCheckedRules, airFareRulesStatus, isModalOpen]);

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-1"
        disabled={isLoading}
        aria-haspopup="dialog"
        aria-expanded={isModalOpen}
      >
        {isLoading ? (
          <>
            <CircularProgress size={12} style={{ color: 'currentColor', marginRight: 4 }} />
            <span>Loading…</span>
          </>
        ) : (
          <>
            <span>Fare Rules</span>
            <FaChevronDown className="inline-block" />
          </>
        )}
      </button>

      {isModalOpen && (
        <FlightFareRulesModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          airFareRulesList={airFareRulesList}
        />
      )}
    </>
  );
};

FareRulesLink.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  flightItinerary: PropTypes.object.isRequired,
  tripId: PropTypes.string.isRequired,
};

export default memo(FareRulesLink);
